import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './ResourcesPage.css';

const TABS = [
  { key: 'sap', label: 'SAP' },
  { key: 'day-in-life', label: 'FedEx Day in Life' },
  { key: 'hiring-workflow', label: 'Hiring Workflow' },
  { key: 'candidate-package', label: 'Candidate Packages' },
  { key: 'fadv-guide', label: 'FADV Guide' },
  { key: 'reg-quals', label: 'Reg. Quals' },
  { key: 'fadv-bg-form', label: 'FADV BG Form' },
  { key: 'release-notes', label: 'Release Notes' },
];

const REGIONAL_QUALS = [
  ['Canada', 'CANADAquals@fedex.com'],
  ['Central', 'CENTRALquals@fedex.com'],
  ['Eastern', 'EASTERNquals@fedex.com'],
  ['Mid-America', 'MIDAMERquals@fedex.com'],
  ['Mid-West', 'MIDWESTquals@fedex.com'],
  ['Northeast', 'NORTHEASTquals@fedex.com'],
  ['Northwest', 'Northwestquals@fedex.com'],
  ['Southeast', 'SOUTHEASTquals@fedex.com'],
  ['Southwest', 'SOUTHWESTquals@fedex.com'],
];

function SapContent() {
  return (
    <div className="rp-sap-card">
      <div className="rp-sap-header">
        <h1>Substance Abuse Professional (SAP) evaluation</h1>
      </div>
      <div className="rp-sap-body">
        <p className="rp-sap-topics">Topics: <strong>Safety</strong></p>

        <p>
          The U.S. Department of Transportation (DOT) offers individuals an opportunity to return to
          safety-sensitive duty after a drug or alcohol violation by undergoing an evaluation with a
          Substance Abuse Professional (SAP) and successfully completing recommended treatment and follow-up.
        </p>

        <p>
          Service provider personnel assigned to operate a CMV (regardless of its size or weight or the class
          of license or permit required to operate it) under an ISPA and who refuse or fail to pass a drug or
          alcohol screening as set forth in the Schedule I may be eligible to be assigned to provide services
          subject to the terms of an SAP evaluation following the 12-month ineligibility period, including:
        </p>

        <ul>
          <li>Initial evaluation and follow-up evaluation by a DOT-certified SAP;</li>
          <li>Recommendation for return-to-duty by SAP; and</li>
          <li>Follow-up drug and alcohol screening schedule.</li>
        </ul>

        <p>
          Upon completion of SAP evaluations and recommended treatment, service providers should submit all
          documentation to <a href="mailto:sap@fedex.com">sap@fedex.com</a>.
        </p>

        <p className="rp-sap-additional-label">Additional information:</p>
        <ul>
          <li>
            FMCSR link explaining SAP:{' '}
            <a href="https://www.fmcsa.dot.gov/regulations/drug-alcohol-testing/substance-abuse-professionals" target="_blank" rel="noopener noreferrer">
              https://www.fmcsa.dot.gov/regulations/drug-alcohol-testing/substance-abuse-professionals
            </a>
          </li>
          <li>
            SAP website:{' '}
            <a href="https://www.go2asap.com" target="_blank" rel="noopener noreferrer">www.go2asap.com</a>
          </li>
        </ul>
      </div>
    </div>
  );
}

function EmbedFrame({ src, title }) {
  return <iframe className="rp-embed-frame" src={src} title={title} />;
}

function PdfEmbed({ src, title }) {
  return (
    <div className="rp-pdf-wrap">
      <div className="rp-pdf-toolbar">
        <a href={src} target="_blank" rel="noopener noreferrer" className="rp-pdf-open-link">
          Open in new tab ↗
        </a>
      </div>
      <iframe className="rp-embed-frame rp-pdf-frame" src={src} title={title} />
    </div>
  );
}

function QualsEmail({ region, email }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {});
  };
  return (
    <div className="rp-quals-item">
      <span className="rp-quals-region">{region}</span>
      <span className="rp-quals-email">{email}</span>
      <button type="button" className="rp-quals-copy" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

// Facility directory is rendered as a plain table (not an embedded PDF) so the browser's own
// Cmd/Ctrl+F finds any abbreviation, number, city or address without clicking into a viewer.
// Data comes from an auth-gated route because the source PDF is marked FedEx Confidential.
function RegQualsContent() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    axios.get(`${API_URL}/api/resources/facility-directory`, { headers })
      .then(res => setRows(res.data))
      .catch(() => setError('Could not load the facility directory.'));
  }, []);

  const regions = useMemo(() => [...new Set((rows || []).map(r => r.region))].sort(), [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (region && r.region !== region) return false;
      if (!q) return true;
      return [r.name, r.abbr, String(r.nbr), r.colo, r.sister, r.status, r.address1, r.address2, r.city, r.state, r.zip, r.district]
        .some(v => v && v.toLowerCase().includes(q));
    });
  }, [rows, query, region]);

  const openPdf = async () => {
    // A plain link can't carry the auth header, so fetch the PDF and open it as a blob URL.
    // The tab is opened first (synchronously) so popup blockers don't eat it after the await.
    const win = window.open('', '_blank');
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.get(`${API_URL}/api/resources/facility-directory/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) win.location.href = url; else window.open(url, '_blank');
    } catch {
      if (win) win.close();
      setError('Could not open the PDF.');
    }
  };

  return (
    <div className="rp-quals">
      <div className="rp-quals-card">
        <h2 className="rp-quals-heading">Regional Quals Emails</h2>
        <div className="rp-quals-grid">
          {REGIONAL_QUALS.map(([r, e]) => <QualsEmail key={e} region={r} email={e} />)}
        </div>
      </div>

      <div className="rp-quals-card">
        <div className="rp-dir-head">
          <h2 className="rp-quals-heading">FedEx Facility Directory</h2>
          <button type="button" className="rp-pdf-open-link rp-dir-pdf-btn" onClick={openPdf}>View original PDF ↗</button>
        </div>
        <div className="rp-dir-controls">
          <input
            type="search"
            className="rp-dir-search"
            placeholder="Search name, abbreviation, number, city, address..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <select className="rp-dir-region" value={region} onChange={e => setRegion(e.target.value)}>
            <option value="">All regions</option>
            {regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {rows && <span className="rp-dir-count">{filtered.length.toLocaleString()} of {rows.length.toLocaleString()}</span>}
        </div>
        {error && <div className="rp-dir-msg rp-dir-error">{error}</div>}
        {!rows && !error && <div className="rp-dir-msg">Loading directory...</div>}
        {rows && (
          <div className="rp-dir-scroll">
            <table className="rp-dir-table">
              <thead>
                <tr>
                  <th>Facility Name</th><th>Abbr</th><th>Nbr</th><th>CoLo</th><th>Sister</th><th>Status</th>
                  <th>Address</th><th>City</th><th>State</th><th>Zip</th><th>Region</th><th>District</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={`${r.abbr}-${r.nbr}`}>
                    <td className="rp-dir-name">{r.name}</td>
                    <td className="rp-dir-abbr">{r.abbr}</td>
                    <td>{r.nbr}</td>
                    <td>{r.colo}</td>
                    <td>{r.sister}</td>
                    <td>{r.status}</td>
                    <td>{r.address1}{r.address2 && <span className="rp-dir-addr2">{r.address2}</span>}</td>
                    <td>{r.city}</td>
                    <td>{r.state}</td>
                    <td>{r.zip}</td>
                    <td>{r.region}</td>
                    <td>{r.district}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="rp-dir-note">Source: FedEx Facility Directory dated Feb 14, 2025. Confirm region/district with local station personnel.</p>
      </div>
    </div>
  );
}

// Release Notes tab (October 2026): list on the left, the selected note on the right. Notes are
// logged-in only (GET /api/resources/release-notes, routes/resources.js) because the screenshots
// show real candidate names; each note is a self-contained HTML page shown in an iframe.
function ReleaseNotesContent() {
  const [notes, setNotes] = useState(null);
  const [active, setActive] = useState(null);
  const [html, setHtml] = useState('');
  const [error, setError] = useState('');
  const frameRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API_URL}/api/resources/release-notes`, { headers })
      .then(r => { setNotes(r.data); if (r.data[0]) setActive(r.data[0].slug); })
      .catch(() => setError('Could not load release notes.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active) return;
    setHtml('');
    axios.get(`${API_URL}/api/resources/release-notes/${active}`, { headers, responseType: 'text' })
      .then(r => setHtml(r.data))
      .catch(() => setError('Could not load this release note.'));
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Grow the frame to the note's full height so the page scrolls, not a box inside it
  const fitFrame = () => {
    const f = frameRef.current;
    if (!f || !f.contentDocument) return;
    const resize = () => { f.style.height = `${f.contentDocument.documentElement.scrollHeight}px`; };
    resize();
    f.contentDocument.querySelectorAll('img').forEach(img => img.addEventListener('load', resize));
    setTimeout(resize, 500);
  };

  return (
    <div className="rn-layout">
      <aside className="rn-list">
        <div className="rn-list-title">Release notes</div>
        {!notes && !error && <div className="rn-muted">Loading…</div>}
        {notes && notes.map(n => (
          <button key={n.slug} className={`rn-item${active === n.slug ? ' active' : ''}`} onClick={() => setActive(n.slug)}>
            <span className="rn-item-title">{n.title}</span>
            <span className="rn-item-meta">{n.dates}{n.audience ? ` · ${n.audience}` : ''}</span>
          </button>
        ))}
      </aside>
      <div className="rn-view">
        {error && <div className="rn-muted">{error}</div>}
        {!error && !html && <div className="rn-muted">Loading…</div>}
        {html && <iframe ref={frameRef} className="rn-frame" title="Release note" srcDoc={html} onLoad={fitFrame} />}
      </div>
    </div>
  );
}

export default function ResourcesPage({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('sap');
  const [loadedTabs, setLoadedTabs] = useState(() => new Set(['sap']));

  const selectTab = (key) => {
    setActiveTab(key);
    setLoadedTabs(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  return (
    <div className="resources-page">
      <SharedNav user={user} onLogout={onLogout} />

      <div className={`dashboard-content${activeTab === 'reg-quals' || activeTab === 'release-notes' ? ' rp-content-wide' : ''}`}>
        <div className="rp-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`rp-tab${activeTab === t.key ? ' active' : ''}`}
              onClick={() => selectTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="rp-tab-panel">
          <div style={{ display: activeTab === 'sap' ? 'block' : 'none' }}>
            {loadedTabs.has('sap') && <SapContent />}
          </div>
          <div style={{ display: activeTab === 'day-in-life' ? 'block' : 'none' }}>
            {loadedTabs.has('day-in-life') && (
              <EmbedFrame src="/resources/fedex-day-in-life.html" title="FedEx Ground P&D Driver — Day in the Life" />
            )}
          </div>
          <div style={{ display: activeTab === 'hiring-workflow' ? 'block' : 'none' }}>
            {loadedTabs.has('hiring-workflow') && (
              <EmbedFrame src="/resources/fedex-hiring-workflow.html" title="FedEx Ground Hiring Workflow" />
            )}
          </div>
          <div style={{ display: activeTab === 'candidate-package' ? 'block' : 'none' }}>
            {loadedTabs.has('candidate-package') && (
              <PdfEmbed src="/resources/candidate-package-guide.pdf" title="Quick Reference Guide — Candidate Package" />
            )}
          </div>
          <div style={{ display: activeTab === 'fadv-guide' ? 'block' : 'none' }}>
            {loadedTabs.has('fadv-guide') && (
              <PdfEmbed src="/resources/fadv-background-screening-guide.pdf" title="FADV Background Screening Quick Guide" />
            )}
          </div>
          <div style={{ display: activeTab === 'reg-quals' ? 'block' : 'none' }}>
            {loadedTabs.has('reg-quals') && <RegQualsContent />}
          </div>
          <div style={{ display: activeTab === 'release-notes' ? 'block' : 'none' }}>
            {loadedTabs.has('release-notes') && <ReleaseNotesContent />}
          </div>
          <div style={{ display: activeTab === 'fadv-bg-form' ? 'block' : 'none' }}>
            {loadedTabs.has('fadv-bg-form') && (
              <PdfEmbed src="/resources/fadv-bg-form-candidate-guide.pdf" title="NGPA Driver Candidate Job Aid: FADV Background Form" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
